import { IDDocumentType } from "@amzn/client-workbench-api-model";
import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, View } from "@amzn/stencil-react-components/layout";
import {
  MessageBanner,
  MessageBannerType,
} from "@amzn/stencil-react-components/message-banner";
import { Text } from "@amzn/stencil-react-components/text";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";

import { AppContext } from "@/stores/appStore";

interface ConfirmIdImageProps {
  /** The document step whose captured still should be confirmed. */
  documentType: IDDocumentType;
  /** Whether document still capture is active for this step. */
  enabled: boolean;
  /** Fired when the new hire confirms the still is legible (for metrics). */
  onConfirm?: () => void;
  /** The submit affordance, revealed only after the new hire confirms. */
  children: JSX.Element;
}

/**
 * Shows the still image captured from the ID recording and asks the new hire to
 * confirm the required details are legible before revealing the submit button.
 * The point is to catch blurry/unreadable captures at the source rather than
 * leaving a human verifier to scrub the video.
 *
 * When capture is disabled or no still was captured (e.g. the OpenCV runtime
 * failed to load, or detection never succeeded), this transparently renders
 * `children`, preserving the video-only flow.
 *
 * Retake is handled by the recorder's existing "Retry" control, which properly
 * resets the recorder and re-runs capture; this component only gates submission.
 */
export default function ConfirmIdImage({
  documentType,
  enabled,
  onConfirm,
  children,
}: ConfirmIdImageProps): JSX.Element {
  const { state } = useContext(AppContext);
  const [confirmed, setConfirmed] = useState(false);

  const idImageBlob = state.documents?.find(
    (doc) => doc.documentType === documentType
  )?.idImageBlob;

  const objectUrl = useMemo(
    () => (idImageBlob ? URL.createObjectURL(idImageBlob) : undefined),
    [idImageBlob]
  );

  // Revoke the object URL when it changes or on unmount to avoid leaking the
  // captured ID image (sensitive PII) in memory.
  useEffect(() => {
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [objectUrl]);

  if (!enabled || !idImageBlob || confirmed) {
    return children;
  }

  return (
    <View
      dataTestId="confirmIdImage"
      padding={{ top: "S300", bottom: "S300" }}
      backgroundColor="color.surface.bg-muted"
      borderRadius="dimensions.border.radius.200"
    >
      <Col gridGap="S300">
        <Text fontSize="T400" fontWeight="bold" color="color.text.primary">
          <FormattedMessage id="identityCheck-confirmIdImage-heading" />
        </Text>
        <View
          border="1px solid color.border.primary"
          borderRadius="dimensions.border.radius.100"
          style={{ overflow: "hidden" }}
        >
          <img
            src={objectUrl}
            alt=""
            data-test-id="confirmIdImagePreview"
            style={{ display: "block", width: "100%" }}
          />
        </View>
        <MessageBanner type={MessageBannerType.Informational}>
          <Text>
            <FormattedMessage id="identityCheck-confirmIdImage-prompt" />
          </Text>
        </MessageBanner>
        <Button
          variant={ButtonVariant.Primary}
          dataTestId="confirmIdImageBtn"
          onClick={() => {
            onConfirm?.();
            setConfirmed(true);
          }}
        >
          <FormattedMessage id="identityCheck-confirmIdImage-confirm" />
        </Button>
        <Text fontSize="T200" color="color.text.secondary">
          <FormattedMessage id="identityCheck-confirmIdImage-retakeHint" />
        </Text>
      </Col>
    </View>
  );
}
